import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MessageCircle, ExternalLink } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { useAuthStore } from '../../stores/authStore'

/**
 * [Telegram bot] Company-facing Telegram page.
 *
 * The bot itself is configured ONCE by the Master Admin, so there is nothing to
 * connect here. This page does two things:
 *   - Connect Telegram — for the signed-in member. Tapping it mints a
 *     single-use, short-lived link that signs them in to the bot without ever
 *     typing their TaskMagix password into a chat (which also means Google-SSO
 *     users, who have no password, can use it).
 *   - For Company Admins: see which of their own members have connected, and
 *     revoke a member's Telegram access.
 */
const sectionCls =
  'rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6'
const headingCls =
  'text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-4'

export function TelegramPage() {
  const qc = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const isCompanyAdmin = role === 'company_admin'

  const me = useQuery({
    queryKey: ['telegram', 'me'],
    queryFn: async () => (await api.get('/telegram/me')).data,
  })
  const links = useQuery({
    queryKey: ['telegram', 'links'],
    queryFn: async () => (await api.get('/telegram/links')).data ?? [],
    enabled: isCompanyAdmin,
  })

  const connect = useMutation({
    mutationFn: async () => (await api.post('/telegram/login-link')).data,
    onSuccess: (data) => {
      if (data?.alreadyConnected) {
        toast.message('Your Telegram account is already connected.')
      } else if (data?.url) {
        // Opening the deep link hands the one-time token straight to Telegram.
        window.open(data.url, '_blank', 'noopener,noreferrer')
        toast.success('Opening Telegram — tap Start to finish signing in.')
      }
      void qc.invalidateQueries({ queryKey: ['telegram', 'me'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const disconnect = useMutation({
    mutationFn: async () => {
      await api.delete('/telegram/link')
    },
    onSuccess: () => {
      toast.success('Telegram disconnected.')
      void qc.invalidateQueries({ queryKey: ['telegram', 'me'] })
      if (isCompanyAdmin) void qc.invalidateQueries({ queryKey: ['telegram', 'links'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const revokeMember = useMutation({
    mutationFn: async (userId) => {
      await api.delete(`/telegram/links/${userId}`)
    },
    onSuccess: () => {
      toast.success('Member’s Telegram access revoked.')
      void qc.invalidateQueries({ queryKey: ['telegram', 'links'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const session = me.data
  const connected = session?.status === 'active'

  return (
    <div>
      <Breadcrumb current="Telegram" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <MessageCircle size={22} className="text-[color:var(--color-accent-strong)]" />
          Telegram
        </h1>
        <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
          Create TaskMagix tasks from Telegram. You’ll only ever see the Spaces and Lists
          your account can already access.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className={sectionCls}>
            <h2 className={headingCls}>Your Telegram</h2>
            {me.isLoading ? (
              <p className="text-sm text-[color:var(--color-ink-muted)] m-0">Loading…</p>
            ) : connected ? (
              <div className="space-y-3">
                <p className="text-sm text-[color:var(--color-ink)] m-0">
                  Connected
                  {session.telegramUsername ? ` as @${session.telegramUsername}` : ''}. Send
                  <code className="px-1">/start</code> to the bot to create a task.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => disconnect.mutate()}
                  loading={disconnect.isPending}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
                  Connect your account to use the bot. We’ll open Telegram with a one-time
                  sign-in link — you never type your password into a chat.
                </p>
                <Button onClick={() => connect.mutate()} loading={connect.isPending}>
                  <ExternalLink size={14} /> Connect Telegram
                </Button>
              </div>
            )}
          </section>

          {isCompanyAdmin ? (
            <section className={sectionCls}>
              <h2 className={headingCls}>Connected members</h2>
              {links.isLoading ? (
                <p className="text-sm text-[color:var(--color-ink-muted)] m-0">Loading…</p>
              ) : links.data?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[28rem]">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-[color:var(--color-ink-muted)]">
                        <th className="py-2 pr-3 font-medium">Member</th>
                        <th className="py-2 pr-3 font-medium">Telegram</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pr-3 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {links.data.map((l) => (
                        <tr key={l.userId} className="border-t border-[color:var(--color-rule)]">
                          <td className="py-2 pr-3 text-[color:var(--color-ink)] [overflow-wrap:anywhere]">
                            {l.userId}
                          </td>
                          <td className="py-2 pr-3 text-[color:var(--color-ink-muted)]">
                            {l.telegramUsername
                              ? `@${l.telegramUsername}`
                              : (l.telegramUserId ?? '—')}
                          </td>
                          <td className="py-2 pr-3 text-[color:var(--color-ink-muted)]">
                            {l.status}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            {l.status === 'active' ? (
                              <button
                                type="button"
                                onClick={() => revokeMember.mutate(l.userId)}
                                className="text-xs text-[color:var(--color-priority-urgent)] hover:underline bg-transparent border-0 cursor-pointer"
                              >
                                Revoke
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
                  No one in your company has connected Telegram yet.
                </p>
              )}
            </section>
          ) : null}
        </div>

        <aside className="space-y-4">
          <section className={sectionCls.replace('p-6', 'p-4')}>
            <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
              How This Works
            </h3>
            <ol className="text-xs text-[color:var(--color-ink)] space-y-1.5 list-decimal pl-4 m-0">
              <li>Tap <strong>Connect Telegram</strong> above and press Start in the bot.</li>
              <li>Send <code>/start</code> to choose a Space.</li>
              <li>Pick a Project or List, then send the task name.</li>
              <li>The bot confirms, and the task appears in that List in TaskMagix.</li>
            </ol>
            <p className="text-xs text-[color:var(--color-ink-muted)] mt-2 mb-0">
              The bot can only do what your account can — it never widens your access.
            </p>
          </section>
        </aside>
      </div>
    </div>
  )
}
